
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SystemHealth as SystemHealthType } from '@/services/dashboardService';
import { Skeleton } from '@/components/ui/skeleton';

interface SystemHealthProps {
  systemHealth: SystemHealthType;
  isLoading: boolean;
}

const SystemHealth: React.FC<SystemHealthProps> = ({ systemHealth, isLoading }) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-md font-medium">System Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((_, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <Skeleton className="h-2 w-full rounded-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 70) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const getTextColor = (percentage: number): string => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 70) return 'text-amber-600';
    return 'text-blue-600';
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-md font-medium">System Health</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Server Uptime</span>
              <span className={`text-sm ${getTextColor(systemHealth.serverUptime)}`}>
                {systemHealth.serverUptime}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`${getProgressColor(systemHealth.serverUptime)} h-2 rounded-full`} 
                style={{ width: `${systemHealth.serverUptime}%` }}
              ></div>
            </div>
          </div>
          
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Database Performance</span>
              <span className={`text-sm ${getTextColor(systemHealth.databasePerformance)}`}>
                {systemHealth.databasePerformance}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`${getProgressColor(systemHealth.databasePerformance)} h-2 rounded-full`} 
                style={{ width: `${systemHealth.databasePerformance}%` }}
              ></div>
            </div>
          </div>
          
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <span className="text-sm font-medium">API Response Time</span>
              <span className={`text-sm ${getTextColor(systemHealth.apiResponseTime)}`}>
                {systemHealth.apiResponseTime}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`${getProgressColor(systemHealth.apiResponseTime)} h-2 rounded-full`} 
                style={{ width: `${systemHealth.apiResponseTime}%` }}
              ></div>
            </div>
          </div>
          
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <span className="text-sm font-medium">Storage Capacity</span>
              <span className={`text-sm ${getTextColor(systemHealth.storageCapacity)}`}>
                {systemHealth.storageCapacity}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`${getProgressColor(systemHealth.storageCapacity)} h-2 rounded-full`} 
                style={{ width: `${systemHealth.storageCapacity}%` }}
              ></div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemHealth;
